"use client";
import {JSX, MouseEvent, ReactNode} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {useMatchMedia} from "@/shared/hooks/dom/use-match-media";
import {useTranslation} from "@/shared/i18n";
import {ButtonSimple} from "@/shared/ui/buttons";
import {ButtonLike} from "@/shared/ui/buttons/button-like";
import {StarRating} from "@/shared/ui/data-display/rating/star-rating";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/info-card.scss";

export interface InfoCardProps {
    id: number | string;
    image: InfoCardImageProps;
    title: string;
    description?: string;
    excerpt?: string;
    url?: string;
    cardType?: "square" | "high" | "landscape";
    readMoreType?: "button" | "arrow" | "text" | "none";
    descriptionTextType?: "normal" | "italic";
    rating?: number;
    fluid?: boolean;
    layout?: "vertical" | "horizontal";
    imagePosition?: "left" | "right";
    category?: {
        id: number | string;
        title: string;
    };
    // Disable all links (image/title/read more) and render as non-link wrappers
    linkDisabled?: boolean;
    // Likes UI (optional, client-only display counters)
    likesEnabled?: boolean;
    likes?: number;
    dislikes?: number;
    // Optional badge at bottom-right of image (e.g., duration)
    badge?: string;
    // Optional footer content (e.g., custom ButtonLike or other elements)
    children?: ReactNode;
    // Click handlers
    onCardClick?: (e: MouseEvent<HTMLElement>) => void;
    onImageClick?: (e: MouseEvent<HTMLElement>) => void;
    onTitleClick?: (e: MouseEvent<HTMLElement>) => void;
}

interface InfoCardImageProps {
    src: string;
    alt: string;
    width?: number;
    height?: number;
}

export function InfoCard({
    image,
    title,
    category,
    description,
    excerpt,
    url = "#",
    cardType = "square",
    readMoreType = "button",
    descriptionTextType = "normal",
    rating,
    fluid = false,
    layout = "vertical",
    imagePosition = "left",
    linkDisabled = false,
    likesEnabled = false,
    likes = 0,
    dislikes = 0,
    badge,
    children,
    onCardClick,
    onImageClick,
    onTitleClick,
}: InfoCardProps): JSX.Element {
    const {t} = useTranslation("common");

    const isMobile = useMatchMedia({maxWidth: 767});
    const imageWidth = image.width ?? 272;
    const imageHeight = image.height ?? 272;
    const textToShow = excerpt ?? description;

    const showCategory = (layout === "vertical" && category) || (layout === "horizontal" && isMobile && category);

    return (
        <article
            className={[
                "info-card",
                cardType && cardType !== "square" ? `info-card_${cardType}` : "",
                fluid ? "info-card_fluid" : "",
                layout === "horizontal" ? "info-card_horizontal" : "",
                layout === "horizontal" && imagePosition === "right" ? "info-card_image-right" : "",
            ]
                .filter(Boolean)
                .join(" ")}
            onClick={onCardClick}
        >
            {linkDisabled ? (
                <div className="info-card__image" aria-label={title} onClick={onImageClick}>
                    <SmartImage src={image.src} alt={image.alt} width={imageWidth} height={imageHeight} />
                    {badge && (
                        <div className="info-card__badge">
                            <Typography as="span" size={{desktop: 10, mobile: 10}} weight={500} color="white">
                                {badge}
                            </Typography>
                        </div>
                    )}
                    {category && (
                        <Typography as="div" size={{desktop: 12, mobile: 10}} className="info-card__category">
                            {category.title}
                        </Typography>
                    )}
                </div>
            ) : (
                <Link href={url} className="info-card__image" aria-label={title} onClick={onImageClick}>
                    <SmartImage src={image.src} alt={image.alt} width={imageWidth} height={imageHeight} />
                    {badge && (
                        <div className="info-card__badge">
                            <Typography as="span" size={{desktop: 10, mobile: 10}} weight={500} color="white">
                                {badge}
                            </Typography>
                        </div>
                    )}
                    {showCategory && (
                        <Typography as="div" size={{desktop: 12, mobile: 10}} className="info-card__category">
                            {category.title}
                        </Typography>
                    )}
                </Link>
            )}
            <div className="info-card__content">
                {linkDisabled ? (
                    <div className="info-card__title-link" onClick={onTitleClick}>
                        <Typography as="h3" size={{desktop: 14, mobile: 10}} className="info-card__title">
                            {title}
                        </Typography>
                    </div>
                ) : (
                    <Link href={url} className="info-card__title-link" onClick={onTitleClick}>
                        <Typography as="h3" size={{desktop: 14, mobile: 10}} className="info-card__title">
                            {title}
                        </Typography>
                    </Link>
                )}

                {typeof rating === "number" && (
                    <div className="info-card__rating">
                        <StarRating name="" rating={rating} size="small" />
                    </div>
                )}

                {textToShow && (
                    <Typography
                        as="p"
                        size={{desktop: 12, mobile: 10}}
                        weight={400}
                        color="paragraph"
                        className={`info-card__description info-card__description_${descriptionTextType}`}
                        innerHTML={textToShow}
                    />
                )}
                {!linkDisabled && readMoreType === "button" && (
                    <div className="info-card__button-wrap">
                        <ButtonSimple as="link" href={url} variant="primary" size="sm">
                            <Typography as="span" size={{desktop: 12, mobile: 8}} options={["underline"]} color="paragraph">
                                {t("readMore")}
                            </Typography>
                        </ButtonSimple>
                    </div>
                )}
                {!linkDisabled && readMoreType === "arrow" && (
                    <div className="info-card__button-wrap">
                        <Link href={url} className="info-card__arrow-link">
                            <SmartImage src={assetPath("/images/icons/arrow-right.svg")} alt="Arrow" width={16} height={16} />
                        </Link>
                    </div>
                )}
                {!linkDisabled && readMoreType === "text" && (
                    <div className="info-card__readmore-text">
                        <Link href={url} className="info-card__title-link">
                            <Typography as="span" size={{desktop: 12, mobile: 10}} color="paragraph" options={["underline"]}>
                                {t("readMore")}
                            </Typography>
                        </Link>
                    </div>
                )}

                {children ? (
                    <div className="info-card__footer">{children}</div>
                ) : likesEnabled ? (
                    <div className="info-card__footer">
                        <ButtonLike likes={likes} dislikes={dislikes} />
                    </div>
                ) : null}
            </div>
        </article>
    );
}
